import { Divider } from 'antd';
import classnames from 'classnames';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCaptureManagerState } from '../CaptureManager/context';
import FullScreenIcon from './components/FullScreenIcon';
import RecordIcon from './components/RecordIcon';
import SettingIcon from './components/SettingIcon';
import SnapshotIcon from './components/SnapshotIcon';
import TimeElapsed from './components/TimeElapsed';
import { useCaptureState, useCaptureUpdater } from './context';
import {
  checkPermissions,
  fixVideoCantScroll,
  getCamera,
  getCameraSpecification,
  getDefaultCamera,
  requestAccess,
  setSrcObject,
  sortingDevices,
} from './helper/webcam-helper';

const Webcam = () => {
  const {
    localVideo,
    selectedResolution,
    snapshot,
    enableMic,
    selectedVideo,
    shortcut,
    isGranted,
    captureWrapper,
    isFullScreen,
  } = useCaptureState();
  const { showCamera } = useCaptureManagerState();
  const dispatch = useCaptureUpdater();
  const { t } = useTranslation('Vindoc');

  const handleDeviceEnumeration = useCallback(async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    if (devices.length === 0) return;

    const [cameraDevices, micDevices] = sortingDevices(devices);
    dispatch({ type: 'SET_CAMERA', payload: cameraDevices });
    dispatch({ type: 'SET_MICROPHONE', payload: micDevices });

    if (cameraDevices.length) {
      const defaultCamera = await getDefaultCamera(
        getCameraSpecification(devices, { position: 'back' })
      );
      dispatch({ type: 'CHANGE_SELECTED_CAMERA', payload: defaultCamera });
    }

    if (micDevices.length) {
      dispatch({ type: 'CHANGE_SELECTED_MICROPHONE', payload: micDevices[0] });
    }
  }, [dispatch]);

  const handlePermissionCheck = useCallback(async () => {
    const isGranted = await checkPermissions(['camera', 'microphone']).catch(console.error);
    if (isGranted) {
      dispatch({ type: 'SET_IS_GRANTED', payload: true });
    } else {
      try {
        await requestAccess();
        dispatch({ type: 'SET_IS_GRANTED', payload: true });
      } catch (e) {
        console.log(`📕 e - 68:Webcam.tsx \n`, e);
        dispatch({ type: 'SET_IS_GRANTED', payload: false });
      }
    }
    await handleDeviceEnumeration();
  }, [dispatch, handleDeviceEnumeration]);

  useEffect(() => {
    if (!showCamera) return;

    handlePermissionCheck();

    return () => {
      dispatch({ type: 'CLEAN' });
    };
  }, [dispatch, handlePermissionCheck, showCamera]);

  useEffect(() => {
    if (!selectedVideo) return;
    let mediaStream = null;

    getCamera(selectedVideo, enableMic)
      .then((stream: MediaStream) => {
        mediaStream = stream;
        setSrcObject(localVideo.current, stream);

        localVideo.current.removeAttribute('controls');

        // Disable click when fullscreen is pause
        localVideo.current.addEventListener('click', (e) => e.preventDefault());

        // Disable when exit fullscreen is paused camera on IOS
        localVideo.current.addEventListener('webkitfullscreenchange', (e) => {
          const event = e.target as HTMLMediaElement;
          if ('webkitIsFullScreen' in document && !document.webkitIsFullScreen && event.paused) {
            setTimeout(() => {
              (async () => {
                await event.play();
              })();
            }, 500);
          }
        });

        fixVideoCantScroll(localVideo.current);
      })
      .catch(console.error);

    return () => {
      if (!mediaStream) return;
      mediaStream?.getTracks?.().forEach((track) => track.stop());
    };
  }, [enableMic, localVideo, selectedResolution, selectedVideo]);

  if (!showCamera) return null;

  return (
    <>
      <div>
        <div
          className={classnames('capture-wrapper')}
          ref={captureWrapper}
          // style={{
          //   aspectRatio: `${selectedResolution.width} / ${selectedResolution.height}`,
          // }}
        >
          <video ref={localVideo} autoPlay playsInline muted className="camera w-full" />
          <TimeElapsed />
          <div className="absolute top-1/2 left-0 flex -translate-y-1/2 transform flex-col space-x-2 p-2" />
          <SnapshotIcon
            disabled={!isGranted}
            title={`${t('Capture Image')} (${shortcut.snapshot})`}
          />
          <RecordIcon disabled={!isGranted} title={`${t('Record Video')} (${shortcut.record})`} />
          <div className="absolute right-[8px] bottom-[8px] flex text-3xl">
            <SettingIcon disabled={!isGranted} />
            <FullScreenIcon
              disabled={!isGranted}
              title={`${t('Fullscreen')} (${shortcut.fullscreen})`}
            />
          </div>
        </div>
        <canvas
          className="hidden max-w-full"
          // width={selectedResolution.width}
          // height={selectedResolution.height}
          ref={snapshot}
        />
      </div>
      <Divider />
      {isFullScreen ? <div className="preview-image-stack" /> : null}
    </>
  );
};

export default Webcam;

/*   const startRecordCanvas = useCallback(() => {
    const canvas = mirrorCanvas.current as HTMLCanvasElement;
    // const ctx = canvas.getContext('2d');

    const stream = canvas.captureStream(60);
    const recorder = new MediaRecorder(stream);

    setMediaRecorder(recorder);

    const chunks = [];

    recorder.ondataavailable = (e) => {
      chunks.push(e.data);
    };

    recorder.onstop = async () => {
      const blob = new Blob(chunks, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);

      recordedVideo.current.src = url;
    };

    recorder.start();
  }, [mirrorCanvas, recordedVideo]);

  const mirror = useCallback(() => {
    const canvas = mirrorCanvas.current;
    const ctx = canvas.getContext('2d');

    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
  }, [mirrorCanvas]); */
